import Slider from "@material-ui/core/Slider";
import { makeStyles } from "@material-ui/core/styles";
import { ChangeEvent, FC, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  loyaltyColourScheme,
  LoyaltyLevel,
} from "../../../components/loyalty/utils";
import { WIDGET_BORDER_RADIUS } from "../../../components/widget";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { Users } from "../../../store/users/reducer";
import { colours } from "../../../styles/colours";

export const ValueLabel = styled.span<{ level?: typeof LoyaltyLevel.T }>`
  padding: 0.4em 0.6em;
  border-radius: ${WIDGET_BORDER_RADIUS};

  background: ${colours.primaryBlue};
  color: ${colours.white};

  ${({ level }) =>
    level !== undefined
      ? `background: ${loyaltyColourScheme[level].main}; color: ${loyaltyColourScheme[level].secondary}`
      : ""};
`;

const useStyles = makeStyles({
  root: {
    width: "100%",
    marginTop: "2em",
    marginBottom: "2em",
    height: "auto",
  },
});

function toLevel(value: number): typeof LoyaltyLevel.T | undefined {
  if (value >= 4) {
    return "PARTNER";
  }
  if (value >= 3) {
    return "EVANGELIST";
  }
  if (value >= 2) {
    return "INSIDER";
  }
  if (value >= 1) {
    return "ADOPTER";
  }
}

function toValue(level: typeof LoyaltyLevel.T | undefined) {
  switch (level) {
    case "ADOPTER":
      return 1;
    case "INSIDER":
      return 2;
    case "EVANGELIST":
      return 3;
    case "PARTNER":
      return 4;
    default:
      return 0;
  }
}

export interface LoyaltyLevelProps {
  level?: typeof LoyaltyLevel.T;
  setLevel: (t: typeof LoyaltyLevel.T | undefined) => void;
}

export const LoyaltySlider: FC<LoyaltyLevelProps> = ({ setLevel, level }) => {
  const currentUser = useFetchable(Users.current);
  const onChange = useCallback(
    (_: ChangeEvent<{}>, value: number | number[]) => {
      setLevel(toLevel(Array.isArray(value) ? value[0] : value));
    },
    [setLevel]
  );

  const marks = useMemo(() => {
    const level = isFetchableSuccess(currentUser)
      ? currentUser.data.level
      : undefined;

    return [
      { value: 0, label: "NONE" },
      { value: 1, label: "ADOPTER" },
      { value: 2, label: "INSIDER" },
      { value: 3, label: "EVANGELIST" },
      { value: 4, label: "PARTNER" },
    ].map(({ value, label }) =>
      (level === undefined && label === "NONE") || level === label
        ? { value, label: <ValueLabel level={level}>{label}</ValueLabel> }
        : { value, label }
    );
  }, [currentUser]);

  const classes = useStyles();
  return (
    <div className={classes.root}>
      <Slider
        value={toValue(level)}
        onChange={onChange}
        orientation="horizontal"
        aria-labelledby="discrete-slider"
        valueLabelDisplay="off"
        step={null}
        marks={marks}
        min={0}
        max={4}
      />
    </div>
  );
};
